import fetcher, {
    fetcherFull,
    MethodEnum,
} from 'utils/helpers/browser/newFetcher';
import { DataResponse, FormResponseData } from 'services/user.service';
import { SocialApiError } from 'services/social/socialErrors';
import {
    AutomationRuleInterface,
    AutomationRuleKindApi,
    AutomationRulePayloadApiInterface,
    PaginatorInterface,
    TeamOptionInterface,
} from 'submodules/api_middleware';

/**
 * The automation shapes travel unchanged through `/api/social/automation/*`
 * (the routes forward the snake_case body verbatim), so they are re-exported
 * here instead of being duplicated client-side — the screen and its components
 * import them from this service like the rest of the Social types.
 */
export type {
    AutomationRuleInterface,
    AutomationRuleKindApi,
    AutomationRulePayloadApiInterface,
    TeamOptionInterface,
};
export type {
    AutomationOutlookItemApiInterface,
    AutomationOutlookMatchApiInterface,
    AutomationRuleNextPostApiInterface,
    AutomationRuleStatsApiInterface,
} from 'submodules/api_middleware';

/** Client-side alias of the wire kind union (`program` | `invitation` | `result`). */
export type AutomationRuleKind = AutomationRuleKindApi;

/** The two channels a post can go to. */
export type SocialChannel = 'facebook' | 'instagram';

/** A club's Facebook page connection. Facebook only — Instagram is a connection of its own. */
export interface FacebookConnectionInterface {
    connection_id: number;
    page_id: string;
    page_name: string;
    /** `Connected`, `NeedsReconnect` or `Disconnected`. */
    status: string;
    data_access_expires_at: string | null;
}

/**
 * A club's Instagram connection: a professional account the club logged in
 * with (Instagram Login). Independent of the Facebook page — a club can have
 * either, both or neither, linked on Meta's side or not.
 */
export interface InstagramConnectionInterface {
    connection_id: number;
    instagram_user_id: string;
    /** Username without `@` (profile: `https://www.instagram.com/{username}/`), or null. */
    instagram_username: string | null;
    /** `Connected`, `NeedsReconnect` or `Disconnected`. */
    status: string;
    /** When the stored token expires (refreshed nightly well before); null when unknown. */
    token_expires_at: string | null;
}

export interface ClubSocialOverviewInterface {
    club_id: number;
    club_name: string;
    /** The Facebook page connection, or null. */
    connection: FacebookConnectionInterface | null;
    /** The Instagram connection, or null. */
    instagram_connection?: InstagramConnectionInterface | null;
}

export interface AuthorizeUrlInterface {
    authorizeUrl: string;
}

/** A page offered after returning from Facebook (`connect/start`). */
export interface FacebookPageOptionInterface {
    page_id: string;
    page_name: string;
    /** `false` when the account may not create content on the page. */
    can_publish: boolean;
}

/** Result of `connect/start`: the pages to choose from + the sealed ticket. */
export interface FacebookPageSelectionInterface {
    selection_token: string;
    club_id: number;
    pages: FacebookPageOptionInterface[];
}

export interface PublishFacebookPostInterface {
    clubId: number;
    message?: string;
    link?: string;
    imageBase64?: string;
    imageFileName?: string;
}

/**
 * Body of the immediate publish to the chosen channels. Instagram needs a JPEG
 * image (render with `format: 'jpeg'`) and a caption that passes
 * `validateInstagramCaption`; with neither channel on the backend answers
 * `social.no_channel_selected`.
 */
export interface PublishPostNowInterface extends PublishFacebookPostInterface {
    publishToFacebook: boolean;
    publishToInstagram: boolean;
}

/** One channel's outcome of an immediate publish. */
export interface ChannelResultInterface {
    /** Whether the channel was asked for at all. */
    requested: boolean;
    /** Why it failed; null when it succeeded or was not requested. */
    error: string | null;
}

/**
 * Result of {@link SocialService.publishNow} — the backend's
 * `PublishPostNowResultDto`, verbatim (snake_case).
 *
 * `status` is `Published` when at least one requested channel went out, so a
 * partial success is `Published` with an `error` on the other channel. Links:
 * Facebook `https://www.facebook.com/{post_id}`, Instagram `permalink` (best
 * effort — it may be null even when `media_id` is set).
 */
export interface PublishPostNowResultInterface {
    /** The post is recorded as a row of the scheduled-posts list. */
    scheduled_post_id: number;
    status: 'Published' | 'Failed';
    facebook: ChannelResultInterface & {
        post_id: string | null;
        photo_id: string | null;
    };
    instagram: ChannelResultInterface & {
        media_id: string | null;
        permalink: string | null;
    };
}

/** Output format of a rendered card; Instagram accepts JPEG only. */
export type SocialImageFormat = 'png' | 'jpeg';

export interface RenderSocialImageInterface {
    templateId: string;
    values: Record<string, any>;
    width: number;
    height: number;
    /**
     * Omitted = `'png'`. Ask for `'jpeg'` whenever the post goes to Instagram;
     * the result's `imageFileName` then ends in `.jpg`.
     */
    format?: SocialImageFormat;
}

export interface RenderSocialImageResultInterface {
    imageBase64: string;
    imageFileName: string;
}

export interface ScheduleFacebookPostInterface {
    clubId: number;
    message?: string;
    link?: string;
    imageBase64?: string;
    imageFileName?: string;
    /** ISO 8601 instant at which the backend should publish the post. */
    scheduledAt: string;
    /** Omitted = true (the backend default). */
    publishToFacebook?: boolean;
    /** Omitted = false. Same rules as {@link PublishPostNowInterface} (JPEG image, caption limits). */
    publishToInstagram?: boolean;
}

export type ScheduledPostStatus =
    | 'Scheduled'
    | 'Publishing'
    | 'Published'
    | 'Failed'
    | 'Cancelled'
    | 'Skipped';

export interface ScheduledPostInterface {
    id: number;
    club_id: number;
    status: ScheduledPostStatus;
    scheduled_at: string;
    published_at: string | null;
    caption: string | null;
    image_file_name: string | null;
    /**
     * Derived server-side from `image_file_name != null`, not from the media
     * blob — so it stays true for published posts whose blob was cleaned up.
     */
    has_image: boolean;
    /** Facebook succeeded when set; `https://www.facebook.com/{fb_post_id}` opens the post. */
    fb_post_id: string | null;
    fb_photo_id: string | null;
    /** Facebook's failure — or the failure of the whole post (e.g. no connection). */
    error_message: string | null;
    /** Channels requested for this post. Pre-Instagram rows: Facebook only. */
    publish_to_facebook: boolean;
    publish_to_instagram: boolean;
    /** Instagram username snapshotted when the post was queued. */
    instagram_username: string | null;
    /** Instagram succeeded when set. */
    ig_media_id: string | null;
    /** Link to the Instagram post; best effort, may stay null after a success. */
    ig_permalink: string | null;
    /** Instagram's failure; null while pending, on success, or when not requested. */
    ig_error_message: string | null;
    /** ISO 8601 instant the Instagram media went out. */
    ig_published_at: string | null;
}

/**
 * List tabs. Each maps to a fixed status set server-side:
 *   upcoming  -> Scheduled, Publishing   (default sort `scheduled_at ASC`)
 *   published -> Published               (default sort `published_at DESC`)
 *   archived  -> Failed, Cancelled       (default sort `scheduled_at DESC`)
 * Omitting the tab returns every status.
 */
export type ScheduledPostsTab = 'upcoming' | 'published' | 'archived';

/** Fields the backend accepts in `sort`; anything else is rejected. */
export type ScheduledPostsSortField =
    | 'scheduled_at'
    | 'published_at'
    | 'status';

/**
 * Filters of the scheduled-posts list, mirroring the URL contract of
 * `/social/scheduled` one-for-one (snake_case, same names as the query string).
 */
export interface ScheduledPostsFilterInterface {
    club_id: number;
    tab?: ScheduledPostsTab;
    /** Fulltext over the post message. */
    query?: string;
    /** ISO 8601 lower bound over `scheduled_at`. */
    from?: string;
    /** ISO 8601 upper bound over `scheduled_at`. */
    to?: string;
    /** 1-based; defaults to 1 server-side. */
    page?: number;
    /** Defaults to 10 server-side, capped at 100. */
    page_size?: number;
    /** `"<field> <ASC|DESC>"`, field from {@link ScheduledPostsSortField}. */
    sort?: string;
}

/** The list payload: one page of rows plus its paginator (`meta.per_page` is always sent here). */
export interface ScheduledPostsListInterface {
    data: ScheduledPostInterface[];
    meta: PaginatorInterface;
}

/** Row counts per tab for the current filters (excluding the tab itself). */
export interface ScheduledPostCountsInterface {
    upcoming: number;
    published: number;
    archived: number;
}

export class SocialService {
    async startConnect(
        clubId: number
    ): Promise<DataResponse<AuthorizeUrlInterface>> {
        // The backend returns a bare `{ authorizeUrl }` (not wrapped in the
        // `{ data: ... }` envelope), so use fetcherFull (full=true) which returns
        // `{ data: <raw body> }` -> response.data.authorizeUrl. Plain fetcher would
        // double-unwrap (.data.data) and lose the url.
        const response = await fetcherFull<DataResponse<AuthorizeUrlInterface>>(
            '/api/social/facebook/authorize-url',
            MethodEnum.GET,
            { clubId }
        );

        if (response?.data?.authorizeUrl) {
            window.pageRedirect(response.data.authorizeUrl);
        }

        return response;
    }

    async getOverview(): Promise<DataResponse<ClubSocialOverviewInterface[]>> {
        return await fetcher<DataResponse<ClubSocialOverviewInterface[]>>(
            '/api/social/facebook/overview',
            MethodEnum.GET
        );
    }

    /**
     * Second step of connecting a page: stores the connection to the page the
     * user picked on `/social/callback`. `selectionToken` is the sealed ticket
     * from `connect/start`. Toasts are left to the caller
     * (`enableToastError = false`) so it can show a localized `social.*`
     * message instead of `getResponse`'s generic one.
     */
    async completeConnect(
        selectionToken: string,
        pageId: string
    ): Promise<FormResponseData> {
        return await fetcherFull<FormResponseData>(
            '/api/social/facebook/connect',
            MethodEnum.POST,
            null,
            { selectionToken, pageId },
            undefined,
            undefined,
            false
        );
    }

    async publish(
        data: PublishFacebookPostInterface
    ): Promise<FormResponseData> {
        return await fetcherFull<FormResponseData>(
            '/api/social/facebook/publish',
            MethodEnum.POST,
            null,
            data
        );
    }

    /**
     * Publishes a post right away to the chosen channels and records it in the
     * scheduled-posts list (as `Published` or `Failed`). Replaces
     * {@link publish} for the composer.
     *
     * Resolves only with the backend's result: a partial success (one channel
     * failed) is still a resolved `Published` result, with the failure in that
     * channel's `error`. Everything that produced no result — a 422 validation
     * error (`social.no_channel_selected`, `social.instagram_requires_jpeg`, …),
     * an auth problem, a timeout — REJECTS with a {@link SocialApiError} whose
     * `code` (and `message`) is the raw backend code, ready for
     * `messageKeyForSocialError`; `'no_code'` when there was none. No toast is
     * pushed here — the caller owns the error message.
     *
     * The timeout sits above the API route's own budget for the backend call
     * (`PUBLISH_NOW_TIMEOUT_MS`, 120 s): both channels run synchronously, and
     * the route's answer must be the one the user sees.
     */
    async publishNow(
        payload: PublishPostNowInterface
    ): Promise<PublishPostNowResultInterface> {
        const response = await fetcherFull<
            DataResponse<PublishPostNowResultInterface> & FormResponseData
        >(
            '/api/social/facebook/publish-now',
            MethodEnum.POST,
            null,
            payload,
            130000,
            undefined,
            false
        );

        const result = response?.data;
        if (
            !response ||
            response.error ||
            !result ||
            typeof result.scheduled_post_id !== 'number' ||
            !result.facebook ||
            !result.instagram
        ) {
            const raw = Array.isArray(response?.error)
                ? response.error[0]
                : response?.error;
            throw new SocialApiError(
                raw || 'no_code',
                response?.status,
                response?.errors
            );
        }

        return result;
    }

    /**
     * Queues a rendered post for later publication. Mirrors {@link publish} but
     * carries an extra `scheduledAt` ISO instant and the channel flags; the
     * render step (producing `imageBase64`) is identical — with
     * `format: 'jpeg'` when Instagram is on — so callers render first, then
     * schedule.
     */
    async schedule(
        data: ScheduleFacebookPostInterface
    ): Promise<FormResponseData> {
        return await fetcherFull<FormResponseData>(
            '/api/social/facebook/schedule',
            MethodEnum.POST,
            null,
            data
        );
    }

    /**
     * One page of a club's scheduled-post queue, ordered by the tab's default
     * sort unless `sort` overrides it.
     *
     * The page itself is rendered from SSR; this is the client-side refetch
     * path (e.g. after a cancel). Note it uses `fetcherFull`: the plain
     * `fetcher` unwraps to `response.data.data` and would throw the paginator
     * away, so the whole `{ data, meta }` envelope is pulled out here instead.
     */
    async getScheduled(
        filter: ScheduledPostsFilterInterface
    ): Promise<ScheduledPostsListInterface> {
        const response = await fetcherFull<
            DataResponse<ScheduledPostsListInterface>
        >('/api/social/facebook/scheduled', MethodEnum.GET, { ...filter });

        return response?.data;
    }

    /**
     * Row counts behind the tab strip. Pass the same filters as
     * {@link getScheduled} minus `tab` — counting within the active tab would
     * zero out every other one.
     */
    async getScheduledCounts(
        filter: Omit<
            ScheduledPostsFilterInterface,
            'tab' | 'page' | 'page_size' | 'sort'
        >
    ): Promise<DataResponse<ScheduledPostCountsInterface>> {
        return await fetcher<DataResponse<ScheduledPostCountsInterface>>(
            '/api/social/facebook/scheduled-counts',
            MethodEnum.GET,
            { ...filter }
        );
    }

    /**
     * Every post in a date window, across all statuses and unpaginated (capped
     * at 500 rows server-side). Backs month-to-month paging in the calendar
     * view without a navigation.
     */
    async getScheduledCalendar(
        clubId: number,
        from: string,
        to: string
    ): Promise<DataResponse<ScheduledPostInterface[]>> {
        return await fetcher<DataResponse<ScheduledPostInterface[]>>(
            '/api/social/facebook/scheduled-calendar',
            MethodEnum.GET,
            { club_id: clubId, from, to }
        );
    }

    /** Cancels a single scheduled post (status -> Cancelled, row stays visible). */
    async cancelScheduled(
        id: number,
        clubId: number
    ): Promise<FormResponseData> {
        return await fetcherFull<FormResponseData>(
            '/api/social/facebook/scheduled',
            MethodEnum.DELETE,
            null,
            { id, club_id: clubId }
        );
    }

    /**
     * Screenshots a template export page to an image via the puppeteer render
     * service — PNG by default, JPEG with `format: 'jpeg'` (required for
     * Instagram) — and returns the raw base64 payload + a file name (`.png` /
     * `.jpg`), ready to hand straight to {@link publishNow} or
     * {@link schedule}. The `/api/social/render` route builds the
     * export-page URL, calls the renderer and replies with the raw
     * `{ imageBase64, imageFileName }` body (so `fetcherFull` wraps it under
     * `data`).
     *
     * The explicit timeout is the outermost rung of the render ladder and has to
     * stay the longest one: the route gives the puppeteer service
     * `SOCIAL_RENDER_TIMEOUT_MS` (45 s by default) and then answers 504 with a
     * message saying so. On `fetcher`'s shared 30 s default the browser would
     * hang up first — the route would see the disconnect, cancel a render that
     * still had 15 s of its budget left, and the user would get the generic
     * network error instead of the route's own explanation.
     */
    async render(
        data: RenderSocialImageInterface
    ): Promise<RenderSocialImageResultInterface> {
        const response = await fetcherFull<
            DataResponse<RenderSocialImageResultInterface>
        >('/api/social/render', MethodEnum.POST, null, data, 60000);

        return response?.data;
    }

    /**
     * All three automation rules of a club (program / invitation / result).
     * The backend answers with the full triplet even for kinds that were never
     * saved, so the screen renders straight off this response; a club outside
     * the user's admin scope comes back empty rather than 401.
     */
    async getAutomationRules(
        clubId: number
    ): Promise<DataResponse<AutomationRuleInterface[]>> {
        return await fetcher<DataResponse<AutomationRuleInterface[]>>(
            '/api/social/automation/rules',
            MethodEnum.GET,
            { club_id: clubId }
        );
    }

    /**
     * Saves one rule and returns it as stored, with `next_run_at`, `stats` and
     * `next_post` recomputed server-side — replace the local copy with the
     * response instead of merging the payload back in. `kind` goes in the query
     * of the API route, which turns it into the backend's path segment.
     */
    async upsertAutomationRule(
        kind: AutomationRuleKindApi,
        clubId: number,
        payload: AutomationRulePayloadApiInterface
    ): Promise<DataResponse<AutomationRuleInterface>> {
        return await fetcher<DataResponse<AutomationRuleInterface>>(
            '/api/social/automation/rules',
            MethodEnum.PUT,
            { club_id: clubId, kind },
            { ...payload }
        );
    }

    /** Team options for a rule's team filter (empty selection = every team). */
    async getAutomationTeams(
        clubId: number
    ): Promise<DataResponse<TeamOptionInterface[]>> {
        return await fetcher<DataResponse<TeamOptionInterface[]>>(
            '/api/social/automation/teams',
            MethodEnum.GET,
            { club_id: clubId }
        );
    }

    async disconnect(
        clubId: number,
        connectionId: number
    ): Promise<FormResponseData> {
        return await fetcherFull<FormResponseData>(
            '/api/social/facebook/disconnect',
            MethodEnum.DELETE,
            null,
            { clubId, connectionId }
        );
    }

    /**
     * Starts connecting the club's Instagram account: redirects the browser to
     * the Instagram login, which returns to `/social/instagram-callback`.
     * Resolves without redirecting (and with `error` set) when the backend
     * refuses, e.g. `social.instagram_login_not_configured`.
     */
    async startInstagramConnect(
        clubId: number
    ): Promise<DataResponse<AuthorizeUrlInterface> & FormResponseData> {
        const response = await fetcherFull<
            DataResponse<AuthorizeUrlInterface> & FormResponseData
        >(
            '/api/social/instagram/authorize-url',
            MethodEnum.GET,
            { clubId },
            undefined,
            undefined,
            undefined,
            false
        );

        if (response?.data?.authorizeUrl) {
            window.pageRedirect(response.data.authorizeUrl);
        }

        return response;
    }

    /** Disconnects the club's Instagram connection; the Facebook page stays as it is. */
    async disconnectInstagram(
        clubId: number,
        connectionId: number
    ): Promise<FormResponseData> {
        return await fetcherFull<FormResponseData>(
            '/api/social/instagram/disconnect',
            MethodEnum.DELETE,
            null,
            { clubId, connectionId }
        );
    }
}
